import React from "react";
import PropTypes from "prop-types";

import * as R from "ramda";

import LinearProgress from "@material-ui/core/LinearProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import { evalAptRangeApi } from "../services/Api";

import { withStyles } from "@material-ui/core/styles";

// Reference Table Cell
const ReferenceTableCell = ({ classes, highlight, range }) => (
  <TableCell className={highlight ? classes.highlight : ""}>{range}</TableCell>
);

ReferenceTableCell.propTypes = {
  classes: PropTypes.object.isRequired,
  highlight: PropTypes.bool,
  range: PropTypes.string.isRequired,
};

ReferenceTableCell.defaultProps = {
  highlight: false,
};

const ReferenceTableCellWithStyles = withStyles((theme) => ({
  highlight: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
}))(ReferenceTableCell);

const rangeToLabels = ({
  ord,
  levelBest,
  levelAboveAvg,
  levelAvg,
  levelBelowAvg,
  levelWorst,
}) => {
  if (ord) {
    return {
      levelBestLabel: `≤ ${levelBest}`,
      levelAboveAvgLabel: `${levelBest} - ${levelAboveAvg - 1}`,
      levelAvgLabel: `${levelAboveAvg} - ${levelAvg - 1}`,
      levelBelowAvgLabel: `${levelAvg} - ${levelBelowAvg - 1}`,
      levelWorstLabel: `≥ ${levelBelowAvg}`,
    };
  } else {
    return {
      levelBestLabel: `≥ ${levelBest}`,
      levelAboveAvgLabel: `${levelAboveAvg} - ${levelBest - 1}`,
      levelAvgLabel: `${levelAvg} - ${levelAboveAvg - 1}`,
      levelBelowAvgLabel: `${levelBelowAvg} - ${levelAvg - 1}`,
      levelWorstLabel: `≤ ${levelBelowAvg - 1}`,
    };
  }
};

const rangeToHighlights = (
  value,
  { ord, levelBest, levelAboveAvg, levelAvg, levelBelowAvg, levelWorst },
  selected
) => {
  let levelBestHighlight = false;
  let levelAboveAvgHighlight = false;
  let levelAvgHighlight = false;
  let levelBelowAvgHighlight = false;
  let levelWorstHighlight = false;

  if (typeof value === "number") {
    if (ord && selected) {
      if (value <= levelBest) levelBestHighlight = true;
      else if (value <= levelAboveAvg) levelAboveAvgHighlight = true;
      else if (value <= levelAvg) levelAvgHighlight = true;
      else if (value <= levelBelowAvg) levelBelowAvgHighlight = true;
      else levelWorstHighlight = true;
    } else if (!ord && selected) {
      if (value >= levelBest) levelBestHighlight = true;
      else if (value >= levelAboveAvg) levelAboveAvgHighlight = true;
      else if (value >= levelAvg) levelAvgHighlight = true;
      else if (value >= levelBelowAvg) levelBelowAvgHighlight = true;
      else levelWorstHighlight = true;
    }
  }

  return {
    levelBestHighlight,
    levelAboveAvgHighlight,
    levelAvgHighlight,
    levelBelowAvgHighlight,
    levelWorstHighlight,
  };
};

// Reference Table Row
const ReferenceTableRow = ({ age, value, range }) => {
  const { minAge, maxAge } = range;

  const selected = typeof age === "number" && age >= minAge && age <= maxAge;

  const {
    levelBestLabel,
    levelAboveAvgLabel,
    levelAvgLabel,
    levelBelowAvgLabel,
    levelWorstLabel,
  } = rangeToLabels(range);

  const {
    levelBestHighlight,
    levelAboveAvgHighlight,
    levelAvgHighlight,
    levelBelowAvgHighlight,
    levelWorstHighlight,
  } = rangeToHighlights(value, range, selected);

  return (
    <TableRow selected={selected}>
      <TableCell>{`${minAge} - ${maxAge}`}</TableCell>
      <ReferenceTableCellWithStyles
        highlight={levelBestHighlight}
        range={levelBestLabel}
      />
      <ReferenceTableCellWithStyles
        highlight={levelAboveAvgHighlight}
        range={levelAboveAvgLabel}
      />
      <ReferenceTableCellWithStyles
        highlight={levelAvgHighlight}
        range={levelAvgLabel}
      />
      <ReferenceTableCellWithStyles
        highlight={levelBelowAvgHighlight}
        range={levelBelowAvgLabel}
      />
      <ReferenceTableCellWithStyles
        highlight={levelWorstHighlight}
        range={levelWorstLabel}
      />
    </TableRow>
  );
};

ReferenceTableRow.propTypes = {
  age: PropTypes.number,
  value: PropTypes.number,
  range: PropTypes.shape({
    minAge: PropTypes.number.isRequired,
    maxAge: PropTypes.number.isRequired,
    ord: PropTypes.bool.isRequired,
    levelBest: PropTypes.number.isRequired,
    levelAboveAvg: PropTypes.number.isRequired,
    levelAvg: PropTypes.number.isRequired,
    levelBelowAvg: PropTypes.number.isRequired,
  }).isRequired,
};

ReferenceTableRow.defaultProps = {
  age: undefined,
  value: undefined,
};

// Reference Table
class ReferenceTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ranges: null,
    };
  }

  async componentDidUpdate(prevProps) {
    const { values } = this.props;

    if (R.isNil(R.path(["customer", "genero"], values))) return;
    if (R.isNil(R.path(["activity", "id"], values))) return;

    if (
      !R.isNil(R.path(["customer", "genero"], prevProps)) &&
      !R.isNil(R.path(["activity", "id"], prevProps)) &&
      values.customer.genero === prevProps.values.customer.genero &&
      values.activity.id === prevProps.values.activity.id
    )
      return;

    const { customer, activity } = values;

    const ranges = await evalAptRangeApi.getList({
      where: { activityId: activity.id },
      order: "minAge asc",
    });

    const filteredRanges = ranges.data.filter((r) =>
      customer.genero === "M" ? r.sex : !r.sex
    );

    filteredRanges.sort((a, b) => a.minAge - b.minAge);

    this.setState({
      ranges: filteredRanges,
    });
  }

  render() {
    const { values } = this.props;

    if (R.isNil(R.path(["customer", "genero"], values)))
      return <span>{"Selecione um cliente"}</span>;

    if (R.isNil(R.path(["activity", "id"], values)))
      return <span>{"Selecione uma atividade"}</span>;

    const { value, ageTaken } = values;
    const { ranges } = this.state;

    if (ranges === null) return <LinearProgress />;

    return (
      <Table padding="checkbox">
        <TableHead>
          <TableRow>
            <TableCell>{"IDADE"}</TableCell>
            <TableCell>{"EXCELENTE"}</TableCell>
            <TableCell>{"BOM"}</TableCell>
            <TableCell>{"MÉDIO"}</TableCell>
            <TableCell>{"REGULAR"}</TableCell>
            <TableCell>{"FRACO"}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ranges.map((v) => (
            <ReferenceTableRow
              key={v.id}
              age={parseInt(ageTaken)}
              value={parseInt(value)}
              range={v}
            />
          ))}
        </TableBody>
      </Table>
    );
  }
}

export default ReferenceTable;
